import { FiChevronRight, FiPlus } from 'react-icons/fi';
import { Listbox, Popover } from '@headlessui/react';
import {
    SelectedBreadcrumb,
    SelectedBreadcrumbLink,
    SelectedBreadcrumbPopOverTrigger,
    SelectedBreadcrumbSelectOnly,
    UnselectedBreadcrumb,
} from './Breadcrumbs/index';
import { SmallBodyRegular, SmallBodyRegularSemi } from './Text';
import { forwardRef, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import Link from './Link';
import Picture from './Picture';
import SemanticList from './SemanticList';
import { SoftButton } from './Button';
import Stack from './Stack';
import Tooltip from './Tooltip';
import { UnstyledButton } from 'utils/fragments';
import { escapeRegExp } from 'utils/helpers';

const GraphOptionMetric = styled.span`
    color: ${({ theme, $isNegative }) =>
        $isNegative === true
            ? theme.red_1
            : $isNegative === false
              ? theme.green_1
              : 'inherit'};
`;

const ActiveStyles = css`
    color: ${({ theme }) => theme.purple_4};
    border-color: ${({ theme }) => theme.purple_4};

    & *,
    & ${GraphOptionMetric} {
        color: ${({ theme }) => theme.purple_1};
    }
`;

const HoverStyles = css`
    &:hover,
    &:focus-visible {
        color: ${({ theme }) => theme.purple_4};
        border-color: ${({ theme }) => theme.purple_4};
    }

    &:hover *,
    &:focus-visible * {
        color: ${({ theme }) => theme.purple_1};
    }

    ${({ $active }) => $active && ActiveStyles}
`;

export const getGraphAttributes = ({ $percentage }) => {
    const percentage = Math.abs($percentage);
    let angle = '90deg';
    let rgba = '152, 100, 255, .1';

    if ($percentage < 0) {
        angle = '270deg';
        rgba = '244, 63, 94, .15';
    }

    return {
        style: {
            backgroundImage: `linear-gradient(${angle},
                rgba(${rgba}) 0%,
                rgba(${rgba}) ${percentage}%,
                rgba(255,255,255,0) ${percentage}%,
                rgba(255,255,255,0) 100%
            )`,
        },
    };
};

const Wrapper = styled.span.attrs(getGraphAttributes)`
    border-radius: ${({ theme }) => theme.borderRadius_lg};
    border: solid 1px ${({ theme }) => theme.purple_7};
    padding: 0px ${({ theme }) => theme.space_5}px;
    color: ${({ theme }) => theme.black};
    font-family: ${({ theme }) => theme.style_primary};
    font-weight: ${({ theme }) => theme.fontBold};
    font-size: 15px;
    line-height: 1.25em;
    min-height: 3.125rem;
    text-transform: capitalize;
    display: inline-flex;
    gap: 0.25rem;
    justify-content: center;
    flex-direction: column;
    text-decoration: none;
    ${HoverStyles}
`;

const VerticalCenter = css`
    display: flex;
    align-items: center;
`;

const NameAndChevronWrapper = styled.span`
    display: flex;
    align-items: stretch;
    justify-content: space-between;
`;

const NameWrapper = styled.span`
    ${VerticalCenter}
    flex-grow: 1;
    overflow: hidden;
`;

const Name = styled.span`
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    hyphens: auto;
    word-break: break-word;
`;

const MetricWrapper = styled.span`
    ${VerticalCenter}
    color: ${({ theme }) => theme.gray_3};
    font-weight: normal;
    text-transform: uppercase;
`;

const Arrow = styled(FiChevronRight)`
    ${VerticalCenter}
    color: ${({ theme }) => theme.gray_4};
    font-size: 1.35em;
    align-self: center;
    flex-shrink: 0;
`;

const MetricsWrapper = styled.div`
    ${VerticalCenter}
    justify-content: space-between;
`;

function GraphButton({
    children,
    primaryMetric,
    secondaryMetric,
    showMetrics = true,
    percentage,
    as = Link,
    ...props
}) {
    return (
        <Wrapper as={as} $percentage={percentage} {...props}>
            <NameAndChevronWrapper>
                <NameWrapper>
                    <Name>{children}</Name>
                </NameWrapper>
                <Arrow
                    css={`
                        margin-right: -6px;
                    `}
                />
            </NameAndChevronWrapper>
            {showMetrics ? (
                <MetricsWrapper>
                    <MetricWrapper>{primaryMetric}</MetricWrapper>
                    {secondaryMetric ? (
                        <MetricWrapper>{secondaryMetric}</MetricWrapper>
                    ) : null}
                </MetricsWrapper>
            ) : null}
        </Wrapper>
    );
}

const GraphOptionWrapper = styled.div`
    border-radius: ${({ theme }) => theme.borderRadius_lg};
    border: solid 1px ${({ theme }) => theme.purple_7};
    color: ${({ theme }) => theme.gray_2};
    font-family: ${({ theme }) => theme.style_primary};
    font-weight: ${({ theme }) => theme.fontBold};
    text-transform: capitalize;
    display: grid;
    grid-template-columns: ${({ $showMetrics }) =>
            $showMetrics ? '6.25rem' : '4.5rem'} 1fr;
    overflow: hidden;
    height: ${({ $showMetrics }) => ($showMetrics ? '5rem' : 'unset')};
`;

const GraphOptionGraph = styled.span.attrs(getGraphAttributes)`
    padding: ${({ theme }) => theme.space_8}px;
    align-self: stretch;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
`;

const GraphOptionRow = styled.div`
    display: flex;
    justify-content: space-between;
`;

const GraphOptionMetricRow = styled(GraphOptionRow)`
    font-size: ${({ theme }) => theme.space_7}px;
    color: ${({ theme }) => theme.gray_3};
    font-weight: normal;
`;

const GraphOptionEl = styled(GraphOptionWrapper)`
    ${HoverStyles}
    cursor: pointer;
`;

export function GraphOption({
    children,
    metrics,
    showMetrics = true,
    percentage,
    image,
    alt,
    forwardedAs = 'option',
    ...props
}) {
    return (
        <GraphOptionEl
            $showMetrics={showMetrics}
            forwardedAs={forwardedAs}
            {...props}
        >
            <Picture
                src={image}
                alt={alt}
                isLoading={!!props.isLoading}
                isDisabled={!!props.isDisabled}
            />
            <GraphOptionGraph $percentage={percentage}>
                <GraphOptionRow>
                    <NameWrapper>
                        <Name>{children}</Name>
                    </NameWrapper>
                    <Arrow />
                </GraphOptionRow>
                {showMetrics ? (
                    <GraphOptionMetricRow>
                        <span>
                            <span
                                css={`
                                    font-weight: ${({ theme }) =>
                                        theme.fontBold};
                                    color: ${({ theme }) => theme.purple_1};
                                    text-transform: uppercase;
                                `}
                            >
                                {metrics[0].metric}
                            </span>{' '}
                            <span>{metrics[0].label}</span>
                        </span>
                        <span>
                            <span>{metrics[1].label}:</span>{' '}
                            <GraphOptionMetric
                                {...(!props.active && {
                                    $isNegative: !!metrics[1].isNegative,
                                })}
                            >
                                {metrics[1].metric}
                            </GraphOptionMetric>
                        </span>
                    </GraphOptionMetricRow>
                ) : null}
            </GraphOptionGraph>
        </GraphOptionEl>
    );
}

const Panel = css`
    position: absolute;
    top: 4px;
    background-color: ${({ theme }) => theme.gray_7};
    box-shadow: 0px 2px 3px rgba(0, 0, 0, 0.07);
    padding: 16px;
    border-radius: 6px;
    width: 440px;
`;

const Overlay = styled.div`
    background-color: rgba(0, 0, 0, 0.15);
    ${({ $open }) =>
        $open &&
        `
        position: fixed;
        inset: 0;
    `}
`;

const space = (theme) => `${theme.space_7}px`;
const BreadcrumbOptions = forwardRef((props, ref) => (
    <Stack css={Panel} as={SemanticList} $space={space} {...props} ref={ref} />
));

export function BreadcrumbSelectMenu({
    options,
    value,
    onChange,
    isActive,
    selectOnly = false,
    defaultOpen = false,
    ...props
}) {
    const disabled = !(options && options.length > 0);
    const buttonRef = useRef(null);
    const hasAutoOpenedRef = useRef(false);

    useEffect(() => {
        if (
            defaultOpen &&
            !hasAutoOpenedRef.current &&
            options &&
            options.length > 0 &&
            !value &&
            buttonRef.current
        ) {
            hasAutoOpenedRef.current = true;
            buttonRef.current.click();
        }
    }, [defaultOpen, options, value]);

    return (
        <Listbox value={value} onChange={onChange} disabled={disabled}>
            {({ open }) => (
                <>
                    {value ? (
                        selectOnly ? (
                            <Listbox.Button
                                aria-label="Change Category"
                                as={SelectedBreadcrumbSelectOnly}
                                isActive={isActive}
                                children={value.name}
                                {...props}
                            />
                        ) : (
                            <SelectedBreadcrumb isActive={isActive}>
                                <SelectedBreadcrumbLink
                                    children={value.name}
                                    {...props}
                                />
                                <Listbox.Button
                                    aria-label="Change Category"
                                    as={SelectedBreadcrumbPopOverTrigger}
                                />
                            </SelectedBreadcrumb>
                        )
                    ) : (
                        <Listbox.Button
                            as={UnselectedBreadcrumb}
                            open={open}
                            ref={buttonRef}
                        >
                            {props.children}
                            {open ? 'Choosing Category' : 'Choose Category'}
                        </Listbox.Button>
                    )}
                    <Overlay $open={open} />
                    {open && (
                        <div css="position: relative;">
                            <Listbox.Options as={BreadcrumbOptions} static>
                                {options.map((option, idx) => (
                                    <Listbox.Option
                                        as="span"
                                        key={option.id || idx}
                                        value={option}
                                    >
                                        {({ active, selected }) => (
                                            <GraphOption
                                                {...option}
                                                $active={active}
                                                $selected={selected}
                                            >
                                                {option.name}
                                            </GraphOption>
                                        )}
                                    </Listbox.Option>
                                ))}
                            </Listbox.Options>
                        </div>
                    )}
                </>
            )}
        </Listbox>
    );
}

const BREADCRUMB_POPOVER_WIDTH = 614;

const BreadcrumbPopoverOptions = styled.div`
    ${Panel}
    padding: 0px;
    font-family: ${({ theme }) => theme.style_primary};
    font-weight: ${({ theme }) => theme.fontBold};
    font-size: ${({ theme }) => theme.space_8}px;
    width: ${BREADCRUMB_POPOVER_WIDTH}px;
`;

const PopoverH1 = styled.h1`
    text-transform: capitalize;
    font-size: 18px;
    font-weight: inherit;
    color: ${({ theme }) => theme.gray_1};
    margin-top: 0px;
`;

const FilterSearch = styled.input`
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
    border: solid 1px ${({ theme }) => theme.purple_7};
    border-radius: 3px;
    color: inherit;
    padding: 8px 12px;
    &::placeholder {
        font-weight: normal;
    }
`;

const BreadcrumbPopover = forwardRef((props, ref) => {
    return (
        <BreadcrumbPopoverOptions
            {...props}
            ref={ref}
        ></BreadcrumbPopoverOptions>
    );
});

export function BreadcrumbPopoverMenu({
    parent,
    options,
    value,
    onChange,
    minVisible = 6,
    status,
    isActive,
    ...props
}) {
    const disabled = !(options && options.length > 0);
    const [numVisible, setNumVisible] = useState(minVisible);
    const [search, setSearch] = useState('');
    const filteredOptions = options.filter(({ name }) => {
        const testResult = new RegExp(escapeRegExp(search), 'gi').test(name);
        return !search || testResult;
    });
    const panelContainer = useRef();

    const [left, setLeft] = useState(0);

    useEffect(() => {
        if (panelContainer.current) {
            const panelRect = panelContainer.current.getBoundingClientRect();
            // TODO: what about when the window resizes?
            const windowInnerWidth = window ? window.innerWidth : null;
            const computedLeft =
                windowInnerWidth - panelRect.left > BREADCRUMB_POPOVER_WIDTH
                    ? 0
                    : windowInnerWidth -
                      BREADCRUMB_POPOVER_WIDTH -
                      panelRect.left;
            if (computedLeft !== left) {
                setLeft(computedLeft);
            }
        }
    });

    return (
        <Popover css="display: flex; flex-direction: column;">
            {({ open, close }) => (
                <>
                    {value ? (
                        <SelectedBreadcrumb isActive={isActive}>
                            <SelectedBreadcrumbLink
                                children={value.name}
                                {...props}
                            />
                            <Popover.Button
                                aria-label="Change Category"
                                as={SelectedBreadcrumbPopOverTrigger}
                                disabled={disabled}
                            />
                        </SelectedBreadcrumb>
                    ) : (
                        <Popover.Button
                            as={UnselectedBreadcrumb}
                            open={open}
                            disabled={disabled}
                        >
                            {props.children}
                        </Popover.Button>
                    )}
                    <Popover.Overlay $open={open} as={Overlay} />
                    <div css="position: relative;" ref={panelContainer}>
                        <Popover.Panel
                            as={BreadcrumbPopover}
                            style={{ left: `${left}px` }}
                        >
                            <Stack
                                $space="12px"
                                css={`
                                    display: flex;
                                    flex-direction: column;
                                    padding: ${({ theme }) => theme.space_8}px
                                        ${({ theme }) => theme.space_9}px;
                                    border-bottom: solid 1px
                                        ${({ theme }) => theme.gray_5};
                                `}
                            >
                                <PopoverH1>
                                    Subcategories for{' '}
                                    <span
                                        css={`
                                            color: ${({ theme }) =>
                                                theme.purple_1};
                                        `}
                                    >
                                        {parent.name}
                                    </span>
                                </PopoverH1>
                                <FilterSearch
                                    type="search"
                                    placeholder="Type here a brand, industry or ingredient"
                                    value={search}
                                    onChange={(e) => {
                                        setNumVisible(minVisible);
                                        setSearch(e.target.value);
                                    }}
                                    disabled={status === 'loading'}
                                />
                            </Stack>
                            <Stack
                                $space={(theme) => `${theme.space_9}px;`}
                                css={`
                                    max-height: 593px;
                                    overflow-y: auto;
                                    padding: ${({ theme }) => theme.space_9}px;
                                `}
                            >
                                {filteredOptions.length < 1 ? (
                                    <div css="display: flex; flex-direction: column;">
                                        <div css="margin: 0px auto;">
                                            There are no subcategories to show.
                                        </div>
                                        {options.length > 0 ? (
                                            <div css="margin: 0px auto;">
                                                Clear your search filter to show{' '}
                                                {options.length} more
                                                subcategories.
                                            </div>
                                        ) : null}
                                    </div>
                                ) : null}
                                {filteredOptions.length > 0 ? (
                                    <>
                                        <div>
                                            <SemanticList
                                                css={`
                                                    display: grid;
                                                    grid-template-columns: repeat(
                                                        2,
                                                        minmax(0, 1fr)
                                                    );
                                                    grid-column-gap: ${({
                                                        theme,
                                                    }) => theme.space_8}px;
                                                    grid-row-gap: ${({
                                                        theme,
                                                    }) => theme.space_8}px;

                                                    & > li {
                                                        display: flex;
                                                        flex-direction: column;
                                                    }
                                                `}
                                            >
                                                {filteredOptions
                                                    .slice(0, numVisible)
                                                    .map((option) => (
                                                        <GraphButton
                                                            as={UnstyledButton}
                                                            key={option.id}
                                                            primaryMetric={
                                                                <GraphButtonMetric
                                                                    metric={
                                                                        option
                                                                            .metrics?.[0]
                                                                            ?.unformattedMetric
                                                                    }
                                                                    label={
                                                                        option
                                                                            .metrics?.[0]
                                                                            ?.label
                                                                    }
                                                                    formatFn={
                                                                        option
                                                                            .metrics?.[0]
                                                                            ?.formatter
                                                                    }
                                                                />
                                                            }
                                                            secondaryMetric={
                                                                <GraphButtonMetric
                                                                    metric={
                                                                        option
                                                                            .metrics?.[1]
                                                                            ?.unformattedMetric
                                                                    }
                                                                    label={
                                                                        option
                                                                            .metrics?.[1]
                                                                            ?.label
                                                                    }
                                                                    formatFn={
                                                                        option
                                                                            .metrics?.[1]
                                                                            ?.formatter
                                                                    }
                                                                    isSigned={
                                                                        true
                                                                    }
                                                                />
                                                            }
                                                            showMetrics={
                                                                option.showMetrics
                                                            }
                                                            percentage={
                                                                option.percentage
                                                            }
                                                            onClick={() => {
                                                                onChange(
                                                                    option,
                                                                );
                                                                close();
                                                            }}
                                                        >
                                                            {option.name}
                                                        </GraphButton>
                                                    ))}
                                            </SemanticList>
                                        </div>
                                        <div css="display: flex; align-items: center; justify-content: center;">
                                            {filteredOptions.length >
                                            minVisible ? (
                                                <SoftButton
                                                    IconLeft={FiPlus}
                                                    onClick={() =>
                                                        setNumVisible(
                                                            numVisible +
                                                                minVisible,
                                                        )
                                                    }
                                                    isDisabled={
                                                        numVisible >=
                                                        filteredOptions.length
                                                    }
                                                >
                                                    Show More {parent.name}
                                                </SoftButton>
                                            ) : null}
                                            {numVisible > minVisible ? (
                                                <>
                                                    <span
                                                        css={`
                                                            border-left: solid
                                                                1px
                                                                ${({ theme }) =>
                                                                    theme.gray_5};
                                                            min-height: 16px;
                                                            margin: 0px 12px;
                                                        `}
                                                    />
                                                    <SoftButton
                                                        onClick={() =>
                                                            setNumVisible(
                                                                minVisible,
                                                            )
                                                        }
                                                    >
                                                        Hide All
                                                    </SoftButton>
                                                </>
                                            ) : null}
                                        </div>
                                    </>
                                ) : null}
                            </Stack>
                        </Popover.Panel>
                    </div>
                </>
            )}
        </Popover>
    );
}

const getMetricColor = ({ theme, $isSigned, $metric }) => {
    if (!$isSigned) return `color: ${theme.gray_900}`;
    if (!$metric) return `color: ${theme.gray_500}`;
    if ($metric < 0) return `&& { color: ${theme.red_1} }`;
    return `&& { color: ${theme.green_1} }`;
};

const Metric = styled(SmallBodyRegularSemi)`
    ${getMetricColor}
`;

const LabelTooltip = styled(SmallBodyRegular)`
    background-color: ${({ theme }) => theme.gray_1};
    color: ${({ theme }) => theme.gray_7};
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    display: flex;
    text-transform: capitalize;
`;
export function GraphButtonMetric({
    metric,
    label,
    formatFn,
    isSigned,
    showLabel = true,
}) {
    return (
        <span>
            {label && !showLabel ? (
                <Tooltip
                    content={label}
                    TooltipBox={LabelTooltip}
                    width="max-content"
                >
                    <Metric $metric={metric} $isSigned={isSigned}>
                        {formatFn(metric)}
                    </Metric>
                </Tooltip>
            ) : (
                <Metric $metric={metric} $isSigned={isSigned}>
                    {formatFn(metric)}
                </Metric>
            )}
            {label && showLabel ? (
                <>
                    &nbsp;
                    <SmallBodyRegular
                        css={`
                            color: ${({ theme }) => theme.gray_700};
                            text-transform: capitalize;
                        `}
                    >
                        {label}
                    </SmallBodyRegular>
                </>
            ) : null}
        </span>
    );
}

export default GraphButton;
